import type {
  AssistantInputAction,
  AssistantMessage,
} from "@triedge/ai-assistant-ui";
import { resolveAssistantArtifactSupport } from "../../shared/assistant-artifact-support";
import {
  ADMISSIONS_THREEJS_VISUALIZATION_PROMPT,
  TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT,
} from "../../shared/assistant-visualization-prompts";
import { resolveAssistantRouteFamily } from "../../config/ai-assistant/route-family-registry";
import type {
  AdmissionsAIContextualAction,
  AdmissionsCitation,
  AdmissionsAssistantJsxEmbed,
  AdmissionsAssistantMessageMetadata,
} from "./types";

export type AssistantToolRegistryActionId =
  | "tool-open-report-builder"
  | "tool-generate-spreadsheet"
  | "tool-generate-chart"
  | "tool-generate-visualization3d"
  | "tool-generate-jsx";

export type AssistantToolDefinition = {
  id: AssistantToolRegistryActionId;
  label: string;
  summary: string;
  inputAction: AssistantInputAction;
  execute: () => void;
};

type AssistantToolRegistryContext = {
  allowReportWorkspace?: boolean;
  pathname: string;
  moduleLabel: string;
  openReportWorkspace: () => void;
  submitQuestion: (question: string) => boolean;
  appendAssistantMessage: (
    message: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>,
  ) => void;
};

const toComponentName = (moduleLabel: string): string =>
  moduleLabel
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("") || "Assistant";

const buildJsxEmbed = (moduleLabel: string): AdmissionsAssistantJsxEmbed => {
  const componentName = `${toComponentName(moduleLabel)}SummaryCard`;
  return {
    componentName,
    headline: `${moduleLabel} JSX embed ready`,
    description:
      "Owned JSX snippet for direct assistant-message embedding. This is previewed in-chat and can be copied into a higher-fidelity workspace later.",
    jsxSource: [
      `export function ${componentName}() {`,
      "  return (",
      '    <section className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">',
      `      <h3 className="text-sm font-semibold text-slate-900">${moduleLabel} Summary</h3>`,
      '      <p className="mt-1 text-xs leading-5 text-slate-600">',
      "        Assistant-generated JSX preview wired through the owned runtime contract.",
      "      </p>",
      "    </section>",
      "  );",
      "}",
    ].join("\n"),
  };
};

const buildRegistryMessage = (
  content: string,
  metadata?: AdmissionsAssistantMessageMetadata,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction> => ({
  role: "assistant",
  content,
  messageId: `assistant-tool-${Date.now()}`,
  timestamp: Date.now(),
  metadata,
});

const resolveVisualization3DToolCopy = (pathname: string): {
  prompt: string;
  summary: string;
  fallbackDescription: string;
} => {
  const routeFamilyId = resolveAssistantRouteFamily(pathname).id;
  if (routeFamilyId === "tuition") {
    return {
      prompt: TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT,
      summary:
        "Run Three.js tuition payment visualization with current-year and prior-year layers.",
      fallbackDescription:
        "Generate an interactive Three.js tuition payment visualization artifact.",
    };
  }
  return {
    prompt: ADMISSIONS_THREEJS_VISUALIZATION_PROMPT,
    summary:
      "Run Three.js admissions visualization against the current dashboard window.",
    fallbackDescription:
      "Generate an interactive Three.js admissions visualization artifact.",
  };
};

export const createAssistantToolRegistry = (
  input: AssistantToolRegistryContext,
): AssistantToolDefinition[] => {
  const visualization3DCopy = resolveVisualization3DToolCopy(input.pathname);
  const spreadsheetSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.spreadsheet.generate",
    question: "Generate a spreadsheet export for the current route.",
  });
  const chartSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.chart.generate",
    question:
      "Make a simple bar chart categorized by status using a Recharts diagram.",
  });
  const visualization3DSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.visualization3d.generate",
    question: visualization3DCopy.prompt,
  });

  const reportWorkspaceTools: AssistantToolDefinition[] =
    input.allowReportWorkspace
      ? [
          {
            id: "tool-open-report-builder",
            label: "Open report builder",
            summary:
              "Open the owned report workspace for template-driven report generation.",
            inputAction: {
              id: "tool-open-report-builder",
              label: "Report Builder",
              description:
                "Open the full report-generation workspace inside the assistant.",
              type: "report_builder",
              actionLabel: "Open",
            },
            execute: () => {
              input.openReportWorkspace();
              input.appendAssistantMessage(
                buildRegistryMessage(
                  `Opened the ${input.moduleLabel} report builder. Templates, filters, exports, and preview all stay inside the owned assistant workspace.`,
                ),
              );
            },
          },
        ]
      : [];
  const visualization3DTools: AssistantToolDefinition[] =
    visualization3DSupport.supported
      ? [
          {
            id: "tool-generate-visualization3d",
            label: "Generate 3D visualization",
            summary: visualization3DCopy.summary,
            inputAction: {
              id: "tool-generate-visualization3d",
              label: "3D Visualization",
              description:
                visualization3DSupport.reason ??
                visualization3DCopy.fallbackDescription,
              type: "artifact_generate",
              actionLabel: "Generate",
            },
            execute: () => {
              input.submitQuestion(visualization3DCopy.prompt);
            },
          },
        ]
      : [];
  const spreadsheetTools: AssistantToolDefinition[] = spreadsheetSupport.supported
    ? [
        {
          id: "tool-generate-spreadsheet",
          label: "Generate spreadsheet",
          summary: "Run spreadsheet generation against the current route context.",
          inputAction: {
            id: "tool-generate-spreadsheet",
            label: "Spreadsheet",
            description:
              spreadsheetSupport.reason ??
              "Generate a spreadsheet artifact from the current route context.",
            type: "artifact_generate",
            actionLabel: "Generate",
          },
          execute: () => {
            input.submitQuestion(
              "Generate a spreadsheet export for the current route.",
            );
          },
        },
      ]
    : [];
  const chartTools: AssistantToolDefinition[] = chartSupport.supported
    ? [
        {
          id: "tool-generate-chart",
          label: "Generate chart",
          summary: "Run chart generation against the current route context.",
          inputAction: {
            id: "tool-generate-chart",
            label: "Chart",
            description:
              chartSupport.reason ??
              "Generate a grounded chart artifact from the current route context.",
            type: "artifact_generate",
            actionLabel: "Generate",
          },
          execute: () => {
            input.submitQuestion(
              "Make a simple bar chart categorized by status using a Recharts diagram.",
            );
          },
        },
      ]
    : [];

  return [
    ...reportWorkspaceTools,
    ...visualization3DTools,
    ...spreadsheetTools,
    ...chartTools,
    {
      id: "tool-generate-jsx",
      label: "Generate JSX embed",
      summary:
        "Create a simple owned JSX snippet and preview it directly inside the assistant transcript.",
      inputAction: {
        id: "tool-generate-jsx",
        label: "JSX Embed",
        description:
          "Preview an owned JSX snippet directly inside the assistant message container.",
        type: "ui_preview",
        actionLabel: "Generate",
      },
      execute: () => {
        input.appendAssistantMessage(
          buildRegistryMessage(
            `Generated a simple JSX embed for ${input.moduleLabel}.`,
            {
              jsxEmbed: buildJsxEmbed(input.moduleLabel),
            },
          ),
        );
      },
    },
  ];
};
